import { z } from 'zod';

export const cardSchema = z.object({
  cardNumber: z
    .string()
    .min(1, 'Card number is required')
    .refine((val) => {
      const digits = val.replace(/\s+/g, '');
      return /^\d{12,19}$/.test(digits);
    }, 'Invalid card number'),
  expirationDate: z
    .string()
    .min(1, 'Expiration date is required')
    .regex(/^(0[1-9]|1[0-2])\/\d{2}$/, 'Expiration date must be in MM/YY'),
  cvv: z
    .string()
    .min(1, 'CVV is required')
    .regex(/^\d{3,4}$/, 'Invalid CVV'),
  tokenizeCard: z.boolean().optional(),
});

export type CardFormValues = z.infer<typeof cardSchema>;

interface ValidationResult {
  isValid: boolean;
  errors: Record<string, string>;
}

const isExpiryDateValid = (expirationDate: string): boolean => {
  const [month, year] = expirationDate.split('/').map(Number);
  if (!month || !year) return false;

  const now = new Date();
  const expiry = new Date(2000 + year, month, 0, 23, 59, 59, 999);

  return expiry >= now;
};

export const validateCardForm = (data: CardFormValues): ValidationResult => {
  const errors: Record<string, string> = {};
  const cardNumber = data.cardNumber.replace(/[\s-]/g, '');

  if (!data.cardNumber) {
    errors.cardNumber = "Card number is required";
  } else if (!/^\d{13,19}$/.test(cardNumber)) {
    errors.cardNumber = "Card number must be between 13 and 19 digits";
  } else if (!/^\d{13,19}$/.test(data.cardNumber.replace(/\s/g, ""))) {
    errors.cardNumber = "Card number must contain only digits";
  }

  if (!data.expirationDate) {
    errors.expirationDate = "Expiration date is required";
  } else if (!/^(0[1-9]|1[0-2])\/\d{2}$/.test(data.expirationDate)) {
    errors.expirationDate = "Expiration date must be in MM/YY format";
  } else if (!isExpiryDateValid(data.expirationDate)) {
    errors.expirationDate = "Expiration date cannot be in the past";
  }

  if (!data.cvv) {
    errors.cvv = "CVV is required";
  } else if (!/^\d{3,4}$/.test(data.cvv)) {
    errors.cvv = "CVV must be 3 or 4 digits";
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
};

export const validatePin = (data: { pin: string }): ValidationResult => {
  const errors: Record<string, string> = {};

  if (!data.pin) {
    errors.pin = "PIN is required";
  } else if (!/^\d{4}$/.test(data.pin)) {
    errors.pin = "PIN must be 4 digits";
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
};

export const validateOtp = (
  data: { otp: string },
  { isAfrigo = false }: { isAfrigo?: boolean } = {}
): ValidationResult => {
  const errors: Record<string, string> = {};

  if (!data.otp) {
    errors.otp = "OTP is required";
  } else if (isAfrigo) {
    if (!/^\d{10}$/.test(data.otp)) {
      errors.otp = "OTP must contain 10 digits";
    }
  } else if (!/^\d{6}$/.test(data.otp)) {
    errors.otp = "OTP must be 6 digits";
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
};

export const validateBirthday = (data: { birthday: string }): ValidationResult => {
  const errors: Record<string, string> = {};

  if (!data.birthday) {
    errors.birthday = "Birthday is required";
  } else if (!/^\d{2}\/\d{2}\/\d{4}$/.test(data.birthday)) {
    errors.birthday = "Birthday must be in DD/MM/YYYY format";
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
};

export const validatePhone = (data: { phone: string }): ValidationResult => {
  const errors: Record<string, string> = {};

  if (!data.phone) {
    errors.phone = "Phone number is required";
  } else if (!/^\d{10,15}$/.test(data.phone)) {
    errors.phone = "Phone number must be between 10 and 15 digits";
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
};

interface AddressForm {
  address: string;
  city: string;
  state: string;
  zipCode: string;
}

export const validateAddress = (form: AddressForm) => {
  const errors: Record<string, string> = {};

  if (!form.address.trim()) {
    errors.address = "Address is required";
  }

  if (!form.city.trim()) {
    errors.city = "City is required";
  }

  if (!form.state.trim()) {
    errors.state = "State is required";
  }

  if (!form.zipCode.trim()) {
    errors.zipCode = "Zip code is required";
  } else if (!/^\d{5}(-\d{4})?$/.test(form.zipCode)) {
    errors.zipCode = "Zip code is invalid";
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
};
